




























































































































































































































import {
  defineComponent,
  reactive,
  computed,
  ref,
  watch,
  Ref,
  onMounted,
} from '@vue/composition-api';
import { useMutation, useQuery } from '@vue/apollo-composable';
import PrettyCheck from 'pretty-checkbox-vue/check';

import { useRouter, useFormat } from '@ideavivat/vivat-2';

import useAuth from '@/useAuth';
import usePayment from '@/usePayment';
import type { ID } from '@/types';
import type { Area } from '@/useDeliveryAreaCode';
import {
  sumDeliveryFee,
  sumProductPrice,
  formatPackage,
} from '@/components/Delivery/useCreateDelivery';
import { DEFAULT_DELIVERY, Package } from '@/useDelivery';
import { computerTime } from '@/utils';

import getTempDelivery from '@/gql/delivery/tempDelivery/getTempDelivery.gql';
import createDelivery from '@/gql/delivery/createDelivery.gql';
import updateDelivery from '@/gql/delivery/updateDelivery.gql';
import findAreaCode from '@/gql/delivery/findAreaCode.gql';
import batchUpdateTempDeliveryStatus from '@/gql/delivery/tempDelivery/batchUpdateTempDeliveryStatus.gql';

import ItemOrder from '@/components/Delivery/ItemOrder.vue';
import ItemOrderForWeight from '@/components/Delivery/ItemOrderForWeight.vue';
import GroupAddressEditor from '@/components/LocationAddress/GroupAddressEditor.vue';
import StickyFooterActionBar from '@/components/StickyFooterActionBar.vue';
import CommonModalWithPreset from '@/components/CommonModalWithPreset.vue';
import CommonTimePicker from '@/components/CommonTimePicker.vue';
import CommonAreaCodeSelector from '@/components/Setting/CommonAreaCodeSelector.vue';
import CommonProductSelector from '@/components/Product/CommonProductSelector.vue';
import VehicleSelectorInput from '@/components/DeliveryNote/VehicleSelectorInput.vue';

const DEFAULT_SELECTED_PRODUCT = {
  areaPriceByProduct: [],
  areaPriceByWeight: [],
  areaPriceByVolume: [],
};

const CommonFormWithDeliveryEditor = defineComponent({
  components: {
    ItemOrder,
    ItemOrderForWeight,
    GroupAddressEditor,
    StickyFooterActionBar,
    CommonModalWithPreset,
    CommonTimePicker,
    CommonAreaCodeSelector,
    CommonProductSelector,
    PrettyCheck,
    VehicleSelectorInput,
  },
  props: {
    edit: {
      default() { return false; },
    },
    byWeight: {
      default() { return false; },
    },
  },
  setup(props: any) {
    const { me } = useAuth();
    const { th: paymentMethodTH, en: paymentMethodEN } = usePayment();
    const toDecimalFormat = (string: any) => parseFloat(string || '0').toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const NOW = new Date();
    const delivery = reactive({ ...DEFAULT_DELIVERY });
    const enableTargetDate = ref(false);
    const selectedProduct = ref({ ...DEFAULT_SELECTED_PRODUCT });
    const vehicleType = ref('');

    const addItem = () => {
      delivery.packages.push({
        id: -1,
        name: '',
        code: '',
        width: '',
        length: '',
        height: '',
        count: 0,
        weight: 0,
        unit: '',
        areaDeliveryFee: 0,
        areaDeliveryFeeByWeight: 0,
        areaDeliveryFeeByVolume: 0,
        deliveryFeePerUnit: 0,
        deliveryFee: 0,
        price: 0,
        itemGroup: '',
      });
      selectedProduct.value = { ...DEFAULT_SELECTED_PRODUCT };
    };
    const deletedPackages: Ref<ID[]> = ref([]);
    const createdPackages = computed(() => delivery.packages.filter((item: any) => (
      item.id === -1
    )).map((item: any) => ({
      ...item,
      id: undefined,
      width: parseFloat(item.width),
      length: parseFloat(item.length),
      height: parseFloat(item.height),
      volume: parseFloat(item.width) * parseFloat(item.length) * parseFloat(item.height) || 0,
      weight: parseFloat(item.weight),
      totalWeight: parseFloat(item.weight) * parseFloat(item.count),
      count: parseFloat(item.count),
      deliveryFee: parseFloat(item.deliveryFee),
      price: parseFloat(item.price),
      deliveryFeePerUnit: undefined,
      deliveryFeeRate: undefined,
      product: undefined,
    })));
    const pop = (index: number) => {
      if (delivery.packages[index]) {
        deletedPackages.value.push(delivery.packages[index].id);
        delivery.packages.splice(index, 1);
      }
    };

    const computedTotalDeliveryFeeBeforeDiscount = computed(() => {
      if (delivery.packages.length === 0) return 0;
      const res = delivery.packages.reduce(sumDeliveryFee as any, 0);
      if (res >= 0) return res;
      return 0;
    });
    const computedTotalDeliveryFee = computed(() => {
      if (delivery.packages.length === 0) return 0;
      const res = computedTotalDeliveryFeeBeforeDiscount.value - parseFloat(delivery.deliveryDiscount as any);
      if (res > 0) return res;
      return 0;
    });

    const computedPackagesInput = computed(() => delivery.packages.map((item: any) => ({
      ...item,
      id: undefined,
      // eslint-disable-next-line
      product_id: item.product.id,
      width: parseFloat(item.width) || 0,
      length: parseFloat(item.length) || 0,
      height: parseFloat(item.height) || 0,
      volume: parseFloat(item.width) * parseFloat(item.length) * parseFloat(item.height) * item.count || 0,
      weight: parseFloat(item.weight) || 0,
      totalWeight: parseFloat(item.weight) * parseFloat(item.count),
      count: parseFloat(item.count),
      deliveryFee: parseFloat(item.deliveryFee),
      price: parseFloat(item.price),
      product: undefined,
      deliveryFeePerUnit: undefined,
      itemGroup: undefined,
      areaDeliveryFee: undefined,
      areaDeliveryFeeByWeight: undefined,
      areaDeliveryFeeByVolume: undefined,
    })));
    const orderInput = computed(() => ({
      orderDate: `${useFormat.date(delivery.orderDate).computerShortDate()} ${computerTime()}`,
      orderTime: undefined,
      customerCode: delivery.customerCode,
      customerName: delivery.customerName,
      contactName: delivery.contactName,
      orderNumber: delivery.orderNumber,
      deliveryDiscount: parseFloat(delivery.deliveryDiscount as any),
      address: {
        ...delivery.address,
        address: delivery.address.address,
        province: delivery.address.province.id,
        district: delivery.address.district.id,
        subdistrict: delivery.address.subdistrict.id,
        zipcode: `${delivery.address.zipcode}`,
        telephone: delivery.address.telephone,
      },
      senderName: delivery.senderName,
      senderCode: delivery.senderCode,
      senderAddress: {
        senderAddress: delivery.senderAddress.address !== '' ? delivery.senderAddress.address : undefined,
        senderProvince: delivery.senderAddress.province.id !== '' ? delivery.senderAddress.province.id : undefined,
        senderDistrict: delivery.senderAddress.district.id !== '' ? delivery.senderAddress.district.id : undefined,
        senderSubdistrict: delivery.senderAddress.subdistrict.id !== '' ? delivery.senderAddress.subdistrict.id : undefined,
        senderZipcode: `${delivery.senderAddress.zipcode}`,
        senderTelephone: delivery.senderAddress.telephone !== '' ? delivery.senderAddress.telephone : undefined,
      },
      // deliveryArea: delivery.orderZone,
      area: (delivery.area) ? { connect: delivery.area } : undefined,
      deliveryFee: computedTotalDeliveryFee.value,
      paymentType: delivery.paymentType,
      remarks: delivery.remarks,
      lat: parseFloat(delivery.lat as any || '0.0'),
      long: parseFloat(delivery.long as any || '0.0'),
      status: props.edit ? undefined : 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER',
      packages: {
        create: computedPackagesInput.value,
      },
      targetDate: enableTargetDate.value ? delivery.targetDate : undefined,
      deliveryCode: delivery.deliveryCode,
      type: delivery.type,
      reservedVehicle: delivery.type === 'LEASED' ? {
        connect: (delivery.reservedVehicle as any).id,
      } : undefined,
      dataSource: 'FAST_API',
    }));

    const refetchQueries = ['getDeliveries', 'getDeliveryNotes', 'tempDeliveries', 'getTempDelivery'];
    const createOrderMutation = useMutation(createDelivery, () => ({
      variables: {
        input: orderInput.value,
      },
      refetchQueries,
    }));

    const { route } = useRouter();

    const { result: tempDeliveryResult, loading } = useQuery(getTempDelivery, computed(() => ({
      id: route.value.params.id,
    })), () => ({ fetchPolicy: 'network-only' }));
    const tempDelivery = computed(() => tempDeliveryResult.value?.tempDelivery || { ...DEFAULT_DELIVERY });
    watch(() => tempDelivery.value, (newVal, oldVal) => {
      if (!loading.value && newVal !== oldVal) {
        delivery.id = tempDelivery.value.id;
        delivery.customerName = tempDelivery.value.customerName;
        delivery.customerCode = tempDelivery.value.customerCode;
        delivery.orderDate = tempDelivery.value.orderDate;
        delivery.contactName = tempDelivery.value.contactName;
        delivery.orderNumber = tempDelivery.value.orderNumber;
        delivery.deliveryDiscount = tempDelivery.value.deliveryDiscount || 0;
        delivery.address = {
          address: tempDelivery.value.address.address || '',
          province: tempDelivery.value.address.province || { id: null },
          district: tempDelivery.value.address.district || { id: null },
          subdistrict: tempDelivery.value.address.subdistrict || { id: null },
          zipcode: tempDelivery.value.address.zipcode,
          telephone: tempDelivery.value.customerPhone,
        };
        delivery.senderName = tempDelivery.value.senderName;
        delivery.senderCode = tempDelivery.value.senderCode;
        delivery.senderAddress = {
          address: tempDelivery.value.senderAddress.address,
          province: tempDelivery.value.senderAddress.province || { id: null },
          district: tempDelivery.value.senderAddress.district || { id: null },
          subdistrict: tempDelivery.value.senderAddress.subdistrict || { id: null },
          zipcode: tempDelivery.value.senderAddress.zipcode !== 'null' ? tempDelivery.value.senderAddress.zipcode : '',
          telephone: tempDelivery.value?.senderPhone || '',
        };
        delivery.area = tempDelivery.value.area?.id || null;
        delivery.deliveryFee = tempDelivery.value.deliveryFee;
        delivery.paymentType = tempDelivery.value.paymentType || 'CASH_BEFORE';
        delivery.remarks = tempDelivery.value.remarks;

        delivery.lat = 0.0000;
        delivery.long = 0.0000;
        delivery.deliveryCode = tempDelivery.value.deliveryCode;

        delivery.type = tempDelivery.value.type;

        if (delivery.type === 'LEASED') {
          vehicleType.value = tempDelivery.value.vehicleType.vehicleType || '';
        }

        if (tempDelivery.value.targetDate && tempDelivery.value.targetDate !== null && tempDelivery.value.targetDate !== '') {
          delivery.targetDate = tempDelivery.value.targetDate;
          enableTargetDate.value = true;
        }
      }
    });

    watch(() => delivery.area, (newVal, oldVal) => {
      interface AreaPrice { area: Area; price: number }
      if (newVal !== oldVal) {
        if (delivery.type !== 'LEASED' && delivery.packages.length > 0) {
          const priceArrayByProduct: AreaPrice[] = [];
          const priceArrayByWeight: AreaPrice[] = [];
          const priceArrayByVolume: AreaPrice[] = [];

          if (delivery.packages.length > 0) {
            delivery.packages[delivery.packages.length - 1].areaDeliveryFee = priceArrayByProduct.find((priceObject: any) => priceObject.area.id === delivery.area)?.price || 0;
            delivery.packages[delivery.packages.length - 1].areaDeliveryFeeByWeight = priceArrayByWeight.find((priceObject: any) => priceObject.area.id === delivery.area)?.price || 0;
            delivery.packages[delivery.packages.length - 1].areaDeliveryFeeByVolume = priceArrayByVolume.find((priceObject: any) => priceObject.area.id === delivery.area)?.price || 0;
          }
        } else {
          const priceArray: AreaPrice[] = selectedProduct.value.areaPriceByProduct || [];
          if (delivery.packages.length > 0) {
            delivery.packages[delivery.packages.length - 1].areaDeliveryFee = priceArray.find((priceObject: any) => priceObject.area.id === delivery.area)?.price || 0;
          }
        }
        if (newVal !== oldVal && !loading.value) {
          delivery.packages = (tempDelivery.value?.packages || []).map((item: any, index: any) => formatPackage(item, delivery.area));
        }
      }
    });
    const computedTotalPrice = computed(() => {
      const total = (delivery.packages as any).reduce(sumProductPrice, 0);
      return total;
    });

    const editOrderMutation = useMutation(updateDelivery, () => ({
      variables: {
        input: {
          ...orderInput.value,
          type: undefined,
          id: route.value.params.id,
          packages: {
            delete: deletedPackages.value,
            create: createdPackages.value,
          },
        },
      },
      refetchQueries,
    }));

    const batchUpdateDeliveryStatusMutation = useMutation(batchUpdateTempDeliveryStatus, () => ({
      variables: {
        ids: route.value.params.id,
        status: 'CONFIRMED',
      },
      refetchQueries,
    }));
    const computedMutation = computed(() => (
      props.edit ? editOrderMutation : createOrderMutation
    ));
    const confirmModal = ref(null);
    createOrderMutation.onDone(() => {
      (confirmModal.value as any).compose({ action: 'บันทึก', entity: 'รายการส่ง สำเร็จ' });
      batchUpdateDeliveryStatusMutation.mutate();
    });
    editOrderMutation.onDone(() => {
      (confirmModal.value as any).compose({ action: 'บันทึก', entity: 'รายการส่ง สำเร็จ' });
    });
    const errorModal = ref(null);
    createOrderMutation.onError(() => {
      (errorModal.value as any).compose();
    });
    editOrderMutation.onError(() => {
      (errorModal.value as any).compose();
    });

    // FIXME: This already mess up, too many duplicate LOC
    const findAreaCodeMutation = useMutation(findAreaCode);
    const foundedArea: Ref<{ id: ID; code: string }[]> = ref([]);
    const availableArea = computed(() => {
      const list = [];
      const display = [];
      for (let i = 0; i < foundedArea.value.length; i += 1) {
        list.push(foundedArea.value[i].id);
        display.push(foundedArea.value[i].code);
      }
      return {
        list,
        display,
      };
    });
    findAreaCodeMutation.onDone((result: any) => {
      foundedArea.value = result.data.findAreaCode;
    });
    function pendingSearchAreaCode() {
      if (delivery.address.province.id && delivery.address.district.id && delivery.address.subdistrict.id) {
        findAreaCodeMutation.mutate({
          provinceId: delivery.address.province.id,
          districtId: delivery.address.district.id,
          subDistrictId: delivery.address.subdistrict.id,
        });
      }
    }
    onMounted(() => {
      setTimeout(() => {
        if (delivery.address.province.id && delivery.address.district.id && delivery.address.subdistrict.id) {
          findAreaCodeMutation.mutate({
            provinceId: delivery.address.province.id,
            districtId: delivery.address.district.id,
            subDistrictId: delivery.address.subdistrict.id,
          });
        }
        delivery.packages = (tempDelivery.value?.packages || []).map((item: any, index: any) => formatPackage(item, delivery.area));
      }, 5000);
    });

    return {
      paymentMethodTH,
      paymentMethodEN,

      delivery,
      selectedProduct,
      vehicleType,
      addItem,
      pop,

      computedMutation,
      confirmModal,
      errorModal,

      deletedPackages,
      createdPackages,

      computedTotalDeliveryFee,
      computedTotalDeliveryFeeBeforeDiscount,
      enableTargetDate,

      computedTotalPrice,

      toDecimalFormat,

      // FIXME: This already mess up, too many duplicate LOC
      findAreaCodeMutation,
      pendingSearchAreaCode,
      availableArea,

      // getProductsAreaDeliveryFee,
    };
  },
});

export default CommonFormWithDeliveryEditor;
